// 入力・読みチップ編集・さがし方（モード/深さ）・特殊モーラトグルの操作群。

import { useEffect, useRef, useState } from 'react'
import type { ReadingToken } from '../nlp/tokenizer'
import { DEFAULT_FLAGS, type Flags } from '../core/toggles'
import { RefinePanel } from './RefinePanel'
import type { RhymeMode, ResultFilter } from '../workers/searchClient'
import type { ReadingStatus } from './useSearchState'
import { readingToKey } from '../core/vowel'

const VOWEL_JP: Record<string, string> = { a: 'あ', i: 'い', u: 'う', e: 'え', o: 'お', N: 'ん', Q: 'っ' }

export function SearchInput({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="ことばを入力（漢字・かな・フレーズ）"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        aria-label="検索する言葉"
        className="w-full rounded-2xl border border-line bg-card px-4 py-4 text-xl text-ink shadow-sm outline-none transition placeholder:text-mut/60 focus:border-accent-vio/60 focus:ring-4 focus:ring-accent-vio/15"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="クリア"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-mut hover:bg-paper hover:text-ink"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}

/** 読み（カタカナ）チップ。タップでインライン編集。読み変換ミスをここで直せるのが差別化の核。 */
export function ReadingChips({
  tokens,
  status,
  onEdit,
}: {
  tokens: ReadingToken[]
  status: ReadingStatus
  onEdit: (index: number, reading: string) => void
}) {
  const [editing, setEditing] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editing !== null) inputRef.current?.focus()
  }, [editing])

  if (status === 'loading') return <div className="text-sm text-mut">読みを解析中…</div>
  if (status === 'error')
    return <div className="text-sm text-rose-600">読み推定に失敗しました。かなで入力してください。</div>
  if (tokens.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-sm">
      <span className="text-mut">読み</span>
      {tokens.map((t, i) =>
        editing === i ? (
          <input
            key={i}
            ref={inputRef}
            defaultValue={t.reading}
            onBlur={(e) => {
              onEdit(i, e.target.value)
              setEditing(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              if (e.key === 'Escape') setEditing(null)
            }}
            aria-label={`${t.surface} の読みを編集`}
            className="w-24 rounded-md border border-accent-vio bg-card px-2 py-1 font-mono text-ink outline-none"
          />
        ) : (
          <button
            key={i}
            type="button"
            onClick={() => setEditing(i)}
            title="タップで読みを修正"
            className="rounded-md border border-line bg-card px-2 py-1 font-mono text-ink transition hover:border-accent-pink/60 hover:text-accent-pink"
          >
            {t.reading || '？'}
          </button>
        ),
      )}
      <span className="ml-1 text-[11px] text-mut">✎ タップで修正</span>
    </div>
  )
}

/** 現在のトグル設定を反映した確定済み母音キーを常時表示する。ん/ー/っの扱いを一目で分かるようにする。 */
export function VowelKeyDisplay({ reading, flags }: { reading: string; flags: Flags }) {
  if (!reading) return null
  const key = readingToKey(reading, flags)
  if (!key) return null
  const jp = [...key].map((c) => VOWEL_JP[c] ?? c).join('・')
  return (
    <div className="text-xs text-mut">
      母音キー <span className="font-mono text-sm font-bold text-accent-vio">{jp}</span>
    </div>
  )
}

const MODES: Array<{ id: RhymeMode; label: string; sub: string }> = [
  { id: 'tail', label: '末尾で踏む', sub: '脚韻' },
  { id: 'head', label: 'あたまで踏む', sub: '頭韻' },
  { id: 'mid', label: 'どこかで踏む', sub: '中間韻' },
  { id: 'exact', label: 'まるごと', sub: '完全一致' },
]

export function ModeSelector({
  mode,
  onChange,
}: {
  mode: RhymeMode
  onChange: (m: RhymeMode) => void
}) {
  return (
    <div className="flex items-stretch overflow-x-auto border-b border-line">
      {MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onChange(m.id)}
          aria-pressed={mode === m.id}
          className={`relative flex-1 whitespace-nowrap px-2 pb-2 pt-1.5 text-center transition ${
            mode === m.id ? 'text-ink' : 'text-mut hover:text-ink'
          }`}
        >
          <div className="text-[13px] font-semibold">{m.label}</div>
          <div className="text-[10px] text-mut">{m.sub}</div>
          {mode === m.id && (
            <span
              aria-hidden
              className="absolute inset-x-1 bottom-0 h-[3px] rounded-full bg-gradient-to-r from-accent-pink to-accent-vio"
            />
          )}
        </button>
      ))}
    </div>
  )
}

/** 踏みの深さ（末尾/先頭で何母音そろえるか）。母音プレビュー付き。 */
export function DepthSlider({
  depth,
  maxDepth,
  queryKey,
  mode,
  onChange,
}: {
  depth: number
  maxDepth: number
  queryKey: string
  mode: RhymeMode
  onChange: (d: number) => void
}) {
  const preview =
    mode === 'head' ? queryKey.slice(0, depth) : queryKey.slice(Math.max(0, queryKey.length - depth))
  const previewJp = [...preview].map((c) => VOWEL_JP[c] ?? c).join(' ')
  return (
    <div className="flex items-center gap-3">
      <label className="shrink-0 text-sm text-mut">
        深さ <span className="font-bold text-accent-vio">{depth}</span>母音
      </label>
      <input
        type="range"
        min={1}
        max={Math.max(1, maxDepth)}
        value={depth}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label="踏みの深さ"
        className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-line accent-accent-vio"
      />
      <span className="min-w-14 shrink-0 text-right font-mono text-accent-vio">{previewJp || '—'}</span>
    </div>
  )
}

interface ToggleDef {
  key: keyof Flags
  mark: string
  on: string
  off: string
  onValue: string
  offValue: string
}
const TOGGLES: ToggleDef[] = [
  { key: 'hatsuon', mark: 'ん', on: 'きっちり', off: 'ゆるく', onValue: 'include', offValue: 'exclude' },
  { key: 'choon', mark: 'ー', on: 'のばす', off: 'のばさない', onValue: 'inherit', offValue: 'ignore' },
  { key: 'sokuon', mark: 'っ', on: '区切り', off: '無視', onValue: 'boundary', offValue: 'skip' },
]

export function SpecialMoraToggles({
  flags,
  onChange,
}: {
  flags: Flags
  onChange: (f: Flags) => void
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {TOGGLES.map((t) => {
          const current = String(flags[t.key])
          const isOn = current === t.onValue
          const label = isOn ? t.on : t.off
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onChange({ ...flags, [t.key]: isOn ? t.offValue : t.onValue })}
              className="flex items-center gap-1.5 rounded-lg border border-line bg-card px-2.5 py-1.5 text-sm transition hover:border-mut/50"
            >
              <span className="font-mono text-base text-ink">{t.mark}</span>
              <span className={isOn ? 'font-semibold text-accent-pink' : 'text-mut'}>{label}</span>
            </button>
          )
        })}
      </div>
      <label className="flex items-center gap-2 rounded-lg bg-paper px-3 py-2 text-sm text-mut">
        <input
          type="checkbox"
          checked={flags.longVowelNorm}
          onChange={(e) => onChange({ ...flags, longVowelNorm: e.target.checked })}
          className="accent-accent-vio"
        />
        長音化ヒューリスティック（トウキョウ＝oooo のように綴りの ou/ei を長音として揃える）
      </label>
    </div>
  )
}

const SLACK_LEVELS = [
  { value: 0, label: 'ぴったり' },
  { value: 1, label: '1ミスOK' },
  { value: 2, label: '2ミスOK' },
]

/** ゆるマッチ（近似韻）の許容ミス数。tail モードでのみ表示する。 */
export function SlackStepper({
  slack,
  onChange,
}: {
  slack: number
  onChange: (v: number) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="shrink-0 text-sm text-mut">ゆるさ</span>
      <div className="grid flex-1 grid-cols-3 gap-1 rounded-xl bg-paper p-1">
        {SLACK_LEVELS.map((l) => (
          <button
            key={l.value}
            type="button"
            onClick={() => onChange(l.value)}
            aria-pressed={slack === l.value}
            className={`rounded-lg px-2 py-1.5 text-sm transition ${
              slack === l.value
                ? 'bg-gradient-to-r from-accent-pink to-accent-vio font-semibold text-white'
                : 'text-mut hover:bg-line/60'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  )
}

interface AdvancedPanelProps {
  rhymeMode: RhymeMode
  slack: number
  onSlackChange: (v: number) => void
  flags: Flags
  onFlagsChange: (f: Flags) => void
  posLegend: string[]
  filter: ResultFilter
  onFilter: (f: ResultFilter) => void
}

const NON_DEFAULT_FLAGS_COUNT = (flags: Flags) =>
  (flags.hatsuon !== DEFAULT_FLAGS.hatsuon ? 1 : 0) +
  (flags.choon !== DEFAULT_FLAGS.choon ? 1 : 0) +
  (flags.sokuon !== DEFAULT_FLAGS.sokuon ? 1 : 0) +
  (flags.longVowelNorm !== DEFAULT_FLAGS.longVowelNorm ? 1 : 0)

/** ゆるさ・特殊モーラ・絞り込みをまとめて折りたたむ「詳細設定」。既定は閉じた状態。
 *  開くと中の全項目を一度に表示する（段階的な入れ子アコーディオンにしない）。 */
export function AdvancedPanel({
  rhymeMode,
  slack,
  onSlackChange,
  flags,
  onFlagsChange,
  posLegend,
  filter,
  onFilter,
}: AdvancedPanelProps) {
  const [open, setOpen] = useState(false)
  const badgeCount =
    (rhymeMode === 'tail' && slack > 0 ? 1 : 0) +
    NON_DEFAULT_FLAGS_COUNT(flags) +
    (filter.pos?.length ? 1 : 0) +
    (filter.moraMin || filter.moraMax ? 1 : 0)

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 text-sm text-mut hover:text-ink"
      >
        詳細設定 {open ? '▲' : '▼'}
        {badgeCount > 0 && (
          <span className="rounded-full bg-accent-vio px-1.5 py-0.5 text-[10px] font-bold text-white">
            {badgeCount}
          </span>
        )}
      </button>
      {open && (
        <div className="space-y-3">
          {rhymeMode === 'tail' && <SlackStepper slack={slack} onChange={onSlackChange} />}
          <SpecialMoraToggles flags={flags} onChange={onFlagsChange} />
          <RefinePanel posLegend={posLegend} filter={filter} onFilter={onFilter} />
        </div>
      )}
    </div>
  )
}
